import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// home1 banner animation
gsap.registerPlugin(ScrollTrigger);

//  fade up animation
let fadeArray_items = document.querySelectorAll(".fade_anim");
if (fadeArray_items.length) {
	const fadeArray = gsap.utils.toArray(".fade_anim");

	fadeArray.forEach((item) => {
		// Set default values and read attributes
		const fade_direction = item.getAttribute("data-fade-from") || "bottom";
		const duration_value =
			parseFloat(item.getAttribute("data-duration")) || 0.75;
		const delay_value = parseFloat(item.getAttribute("data-delay")) || 0.15;
		const fade_offset = 40;
		const ease_value = "power2.out";

		let animation_settings = {
			opacity: 0,
			ease: ease_value,
			duration: duration_value,
			delay: delay_value,
		};

		// Apply direction-based offsets
		if (fade_direction === "top") animation_settings.y = -fade_offset;
		if (fade_direction === "left") animation_settings.x = -fade_offset;
		if (fade_direction === "bottom") animation_settings.y = fade_offset;
		if (fade_direction === "right") animation_settings.x = fade_offset;

		// Add ScrollTrigger if necessary
		animation_settings.scrollTrigger = {
			trigger: item,
			start: "top 90%",
		};

		// Apply animation
		gsap.from(item, animation_settings);
	});
}

// reveal animation
function revealAnim(selector) {
	document.querySelectorAll(selector).forEach((item) => {
		const direction = item.getAttribute("data-reveal-from") || "left";
		const delay = parseFloat(item.getAttribute("data-delay")) || 0;
		const duration = parseFloat(item.getAttribute("data-duration")) || 1.2;

		// Set will-change for better performance
		item.style.willChange = "opacity, transform, clip-path";

		// Define initial clip-path values based on direction
		const clipPaths = {
			top: "inset(100% 0% 0% 0%)",
			right: "inset(0% 0% 0% 100%)",
			bottom: "inset(0% 0% 100% 0%)",
			left: "inset(0% 100% 0% 0%)",
		};

		gsap.fromTo(
			item,
			{
				opacity: 0,
				clipPath: clipPaths[direction] || clipPaths.left, // Default to left if invalid
			},
			{
				opacity: 1,
				clipPath: "inset(0% 0% 0% 0%)",
				duration,
				delay,
				ease: "power3.out",
				scrollTrigger: {
					trigger: item,
					start: "top 85%",
					toggleActions: "play none none none",
					once: true, // Prevents re-triggering
				},
				onComplete: () => (item.style.willChange = "auto"), // Reset will-change
			},
		);
	});
}

// text split animation
function splitTextAnimation(selector) {
	document.querySelectorAll(selector).forEach((item) => {
		const delay = parseFloat(item.getAttribute("data-delay")) || 0;
		const duration = parseFloat(item.getAttribute("data-duration")) || 0.6;

		// Set will-change for better performance
		item.style.willChange = "transform, opacity";

		// Split text into words and letters, wrapping in spans
		const words = item.innerText
			.split(" ")
			.map((word) => {
				return `<span style="white-space: nowrap; display: inline-block; overflow: hidden;">${[
					...word,
				]
					.map(
						(letter) =>
							`<span style="display: inline-block; opacity: 0; transform: translateY(80px) rotateX(120deg) scale(0.8); filter: blur(5px);">${letter}</span>`,
					)
					.join("")}</span>`;
			})
			.join(" ");

		// Update innerHTML in one operation
		item.innerHTML = words;

		const letters = item.querySelectorAll("span span"); // Selects all inner letter spans

		// Defer animation to next frame for better performance
		requestAnimationFrame(() => {
			gsap.to(letters, {
				opacity: 1,
				y: 0,
				rotationX: 0,
				scale: 1,
				filter: "blur(0px)", // Gradually remove blur
				duration: duration,
				delay: delay,
				ease: "power4.out", // Smoother, more dramatic easing
				stagger: {
					each: 0.035 + Math.random() * 0.02, // Slight randomization for a natural feel
					from: "start",
				},
				scrollTrigger: {
					trigger: item,
					start: "top 90%",
					toggleActions: "play none none none",
					once: true, // Ensures the animation runs only once
				},
				onComplete: () => (item.style.willChange = "auto"), // Reset for performance
			});
		});
	});
}

// scale and fade animation
function scaleTextReveal(selector) {
	document.querySelectorAll(selector).forEach((item) => {
		const delay = parseFloat(item.getAttribute("data-delay")) || 0;
		const duration = parseFloat(item.getAttribute("data-duration")) || 1.5;

		// Set will-change for better performance
		item.style.willChange = "transform, opacity";

		// Split text into words and wrap letters in span elements
		const words = item.innerText
			.split(" ")
			.map((word) => {
				return `<span style="white-space: nowrap; display: inline-block; overflow: hidden;">${[
					...word,
				]
					.map(
						(letter) =>
							`<span style="display: inline-block; opacity: 0; transform: translateY(50px) scale(0.8) rotate(-5deg); filter: blur(4px);">${letter}</span>`,
					)
					.join("")}</span>`;
			})
			.join(" ");

		// Update innerHTML in one operation to minimize reflows
		item.innerHTML = words;

		const letters = item.querySelectorAll("span span"); // Select all inner letter spans

		// Defer animation to next frame for better performance
		requestAnimationFrame(() => {
			gsap.to(letters, {
				opacity: 1,
				scale: 1,
				y: 0,
				rotate: 0, // Remove initial tilt smoothly
				filter: "blur(0px)", // Remove blur effect gradually
				duration: duration,
				delay: delay,
				ease: "elastic.out(1, 0.6)", // Smooth but controlled bounce
				stagger: {
					each: 0.035 + Math.random() * 0.02, // Natural variation in timing
					from: "start",
				},
				scrollTrigger: {
					trigger: item,
					start: "top 90%",
					toggleActions: "play none none none",
					once: true, // Only play once on scroll
				},
				onComplete: () => {
					item.style.willChange = "auto"; // Reset for performance
				},
			});
		});
	});
}

// blur text animation
function blurTextReveal(selector) {
	document.querySelectorAll(selector).forEach((item) => {
		const delay = parseFloat(item.getAttribute("data-delay")) || 0; // Default delay
		const duration = parseFloat(item.getAttribute("data-duration")) || 0.8; // Default duration

		// Set will-change for better performance
		item.style.willChange = "transform, opacity, filter";

		// Split text into words and wrap letters in span elements
		const words = item.innerText
			.split(" ")
			.map((word) => {
				return `<span style="white-space: nowrap; display: inline-block;">${[
					...word,
				]
					.map(
						(letter) =>
							`<span style="display: inline-block; opacity: 0; filter: blur(10px); transform: translateY(5px);">${letter}</span>`,
					)
					.join("")}</span>`;
			})
			.join(" ");

		// Update innerHTML in one operation to minimize reflows
		item.innerHTML = words;

		const letters = item.querySelectorAll("span span"); // Select all inner letter spans

		// Defer animation to the next frame for smoother transitions
		requestAnimationFrame(() => {
			gsap.to(letters, {
				opacity: 1,
				y: 0,
				filter: "blur(0px)", // Removes blur effect on letters
				duration: duration,
				delay: delay,
				ease: "power3.out", // Smooth easing for the animation
				stagger: 0.03, // Continuous stagger across all letters for a flowing effect
				scrollTrigger: {
					trigger: item,
					start: "top 85%",
					toggleActions: "play none none none", // Trigger actions for scroll-based animation
					once: true, // Ensures the animation only triggers once
				},
				onComplete: () => {
					item.style.willChange = "auto"; // Reset will-change after animation completes
				},
			});
		});
	});
}

// explode text animation
function explodeTextReveal(selector) {
	document.querySelectorAll(selector).forEach((item) => {
		const delay = parseFloat(item.getAttribute("data-delay")) || 0;
		const duration = parseFloat(item.getAttribute("data-duration")) || 0.5;

		// Store text content in a variable
		const textContent = item.textContent;

		// Create a document fragment to batch DOM updates
		let fragment = document.createDocumentFragment();

		textContent.split(" ").forEach((word) => {
			let wordSpan = document.createElement("span");
			wordSpan.style.whiteSpace = "nowrap";
			wordSpan.style.display = "inline-block";

			[...word].forEach((letter) => {
				let letterSpan = document.createElement("span");
				letterSpan.textContent = letter;
				letterSpan.style.display = "inline-block";
				letterSpan.style.opacity = "0";
				letterSpan.style.transform =
					"translateY(20px) scale(0.8) rotate(10deg)";
				letterSpan.style.willChange = "transform, opacity"; // Performance boost
				wordSpan.appendChild(letterSpan);
			});

			fragment.appendChild(wordSpan);
			fragment.appendChild(document.createTextNode(" ")); // Preserve spaces
		});

		// Replace inner content in one step
		item.innerHTML = "";
		item.appendChild(fragment);

		const letters = item.querySelectorAll("span span");

		// Set initial styles using GSAP to avoid forced reflows during animation
		gsap.set(letters, {
			opacity: 0,
			transform: "translateY(20px) scale(0.8) rotate(10deg)",
		});

		// Defer animation to the next frame using requestAnimationFrame to prevent forced reflows
		requestAnimationFrame(() => {
			gsap.to(letters, {
				scale: 1,
				opacity: 1,
				y: 0,
				rotate: 0,
				duration: duration,
				delay: delay,
				ease: "elastic.out(1, 0.6)",
				stagger: {
					each: 0.05, // More natural reveal
					from: "random",
				},
				scrollTrigger: {
					trigger: item,
					start: "top 85%",
					toggleActions: "play none none none",
					once: true, // Prevents multiple reflows
				},
			});
		});
	});
}

function slidingBlurCardReveal(selector) {
	document.querySelectorAll(selector).forEach((item) => {
		const delay = parseFloat(item.getAttribute("data-delay")) || 0; // Default delay
		const duration = parseFloat(item.getAttribute("data-duration")) || 1.2; // Default duration

		// Apply will-change for performance optimization
		item.style.willChange = "transform, opacity, filter";

		// Set initial hidden state
		gsap.set(item, { opacity: 0, filter: "blur(10px)", y: 50 });

		// Animate the card with smooth transition on scroll
		gsap.to(item, {
			opacity: 1,
			y: 0,
			filter: "blur(0px)",
			duration: duration,
			delay: delay,
			ease: "power3.out",
			scrollTrigger: {
				trigger: item,
				start: "top 90%", // Start animation slightly earlier for smoother effect
				end: "bottom 50%", // Keep animation active while in view
				toggleActions: "play none none none",
				once: true, // Ensures the animation triggers only once
				onEnter: () => (item.style.willChange = "auto"), // Reset after animation completes
			},
		});
	});
}

// Initialize the animation on elements
slidingBlurCardReveal(".card_anim");
blurTextReveal(".blur_anim");
scaleTextReveal(".scale_anim");
revealAnim(".reveal_anim");
splitTextAnimation(".split_anim");
explodeTextReveal(".explode_anim");



const fish = document.querySelectorAll('.fish');
fish.forEach(fishElement => {
	gsap.from(fishElement, {
		x: fishElement.classList.contains('fish-left') ? -200 : fishElement.classList.contains('fish-right') ? 200 : 0,
		y: fishElement.classList.contains('fish-top') ? -200 : 0,
		duration: 4,
		repeat: -1,
		yoyo: true,
		ease: "back.out"
	});
});
